import React from 'react';
import { CircularProgress, Stack, Typography, styled } from '@mui/material';
import { CareProgramsList } from 'components/CareProgramsList/CareProgramsList';
import { ErrorState } from 'components/ErrorState/ErrorState';
import { ResourceSearchEmptyState } from 'components/Resources/ResourceSearchEmptyState';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useCareProgramSearch } from 'features/resources/hooks';
import type { Program } from 'features/resources/types';
import NetworkError from 'assets/images/ErrorState/NetworkError.svg';

const CenteredState = styled(Stack)(({ theme }) => ({
  width: '100%',
  minHeight: '100%',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(8, 2),
}));

export interface CareProgramSearchResultsProps {
  searchTerm: string;
  onProgramClick?: (program: Program) => void;
  selectedProgramId?: number;
}

export const CareProgramSearchResults: React.FC<CareProgramSearchResultsProps> = ({
  searchTerm,
  onProgramClick,
  selectedProgramId,
}) => {
  const { translation } = useTranslationContext();
  const { sections, isLoading, error, refetch, resultCount } = useCareProgramSearch(searchTerm);

  if (error) {
    return (
      <ErrorState
        imageArray={[NetworkError]}
        onRetryClick={refetch}
        title={translation('no_connection_overlay')}
        subtitle={translation('error_loading_body')}
      />
    );
  }

  if (!searchTerm.trim()) {
    return null;
  }

  if (isLoading) {
    return (
      <CenteredState role="status">
        <CircularProgress size={48} />
        <Typography variant="callout" color="grey.800">
          {translation('loading_search_results')}
        </Typography>
      </CenteredState>
    );
  }

  if (sections.length === 0) {
    return (
      <CenteredState>
        <ResourceSearchEmptyState />
      </CenteredState>
    );
  }

  return (
    <CareProgramsList
      sections={sections}
      onProgramClick={onProgramClick}
      selectedProgramId={selectedProgramId}
      resultsText={`${translation('search_results')} (${resultCount})`}
    />
  );
};
